<script lang="ts" setup>
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch, watchEffect, nextTick, getCurrentInstance } from 'vue'
import { VExpansionPanel, VExpansionPanels, VExpansionPanelText, VExpansionPanelTitle } from 'vuetify/components'

import LayoutContainer from '../Layout/LayoutContainer.vue'
import LayoutItem from '../Layout/LayoutItem.vue'
import FieldsGridContent from '../LayoutGrid/FieldsGridContent.vue'
import { getGridEntries } from '../LayoutGrid/utils'

import type { BaseFieldClass } from '@/models/fields/baseField.class'
import type { IGridAreas } from '@/types/serverGrid.type'

import { SectionClass, SectionsClass } from '@/api/fields'
import { useEventListeners } from '@/composables/useEventListeners'
import { useFieldChangeConfirm } from '@/composables/useFieldChangeConfirm'
import { EnumEventTypes } from '@/constants/eventTypes.constants'

const FieldTypeText = defineAsyncComponent(() => import('./FieldTypeText.vue'))
const FieldTypeTinymce = defineAsyncComponent(() => import('./FieldTypeTinymce.vue'))
const FieldTypeList = defineAsyncComponent(() => import('./FieldTypeList.vue'))
const FieldTypeDate = defineAsyncComponent(() => import('./FieldTypeDate.vue'))
const FieldTypeData = defineAsyncComponent(() => import('./FieldTypeData.vue'))
const FieldTypeWidget = defineAsyncComponent(() => import('./FieldTypeWidget.vue'))
const FieldTypeСodeMirror = defineAsyncComponent(() => import('./FieldTypeСodeMirror.vue'))
const FieldTypeFileList = defineAsyncComponent(() => import('./FieldTypeFileList.vue'))
const FieldTypeCheckBox = defineAsyncComponent(() => import('./FieldTypeCheckBox.vue'))
const FieldTypeColor = defineAsyncComponent(() => import('./FieldTypeColor.vue'))
const FieldTypePeriod = defineAsyncComponent(() => import('./FieldTypePeriod.vue'))
const FieldTypeButton = defineAsyncComponent(() => import('./FieldTypeButton.vue'))

const FieldChangeConfirmDialog = defineAsyncComponent(() => import('./FieldChangeConfirmDialog.vue'))

// По ключу можем получить необходимый для нас компонент
const componentsMap = {
  'type-text': FieldTypeText,
  'type-tinymce': FieldTypeTinymce,
  'type-list': FieldTypeList,
  'type-date': FieldTypeDate,
  'type-data': FieldTypeData,
  'type-widget': FieldTypeWidget,
  'type-code-mirror': FieldTypeСodeMirror,
  'type-file-list': FieldTypeFileList,
  'type-checkbox': FieldTypeCheckBox,
  'type-color': FieldTypeColor,
  'type-period': FieldTypePeriod,
  'type-button': FieldTypeButton
}

const componentTypeMap: Record<string, keyof typeof componentsMap> = {
  bool: 'type-checkbox',
  string: 'type-text',
  email: 'type-text',
  text: 'type-text',
  tinyMCE: 'type-tinymce',
  date: 'type-date',
  listInt: 'type-list',
  Widget: 'type-widget',
  fileList: 'type-file-list',
  imageList: 'type-file-list',
  codeMirror: 'type-code-mirror',
  color: 'type-color',
  data: 'type-data',
  int: 'type-text',
  float: 'type-text',
  password: 'type-text',
  period: 'type-period',
  button: 'type-button'
}

// Получить имя компонента по типу поля
// Если тип поля для нас неизвестен - получим null
const getComponentName = (type: string) => {
  const key = componentTypeMap[type] || `type-${type}`
  return componentsMap[key] || null
}

const emit = defineEmits(['change'])

const props = defineProps<{
  modelValue: SectionsClass
  config?: any,
  bordered?: boolean,
  uuid?: string | undefined,
  listenersIsInitialized?: boolean,
  dispatchOnMount?: boolean,
  displaySections?: SectionClass[],
  presentationOnly?: boolean,
  ignoreLayout?: boolean,
  hideSectionTitles?: boolean,
  sectionsGrid?: IGridAreas | null
}>()

const sectionsRef = ref<SectionsClass>(props.modelValue)

watchEffect(() => {
  sectionsRef.value = props.modelValue
})

const sections = computed<SectionClass[]>(() => {
  const all: SectionClass[] = props.displaySections ?? sectionsRef.value?.sections ?? []

  return all.filter(section => {
    if (section.formLayout.visible === false) return false

    // есть дочерние секции - выводим
    if (section.children.length > 0) return true

    // есть хотя бы одно видимое + не скрытое поле - выводим
    return section.fields.some(f => f.type !== 'hidden' && f.visible !== false)
  })
})

const sectionsGrid = computed(() => props.ignoreLayout ? null : (props.sectionsGrid ?? (props.presentationOnly ? null : sectionsRef.value?.sectionsGrid)))
const config = props.config

const filterFields = (fields: BaseFieldClass[]) => {
  return fields.filter(field => field.type !== 'hidden' && field.type !== 'empty' && field.visible && field.layout.visible)
}

const setSectionVisible = (index: number, value: boolean) => {
  const section = sectionsRef.value?.getSectionById(index)

  if (section) {
    section.visible = value
  }
}

const uid = getCurrentInstance()?.uid

// Управление отложенным изменением значения и окном подтверждения.
const {
  pendingFieldChange,
  getEditableField,
  getEditableFieldKey,
  requestFieldUpdate,
  applyPendingFieldChange: commitPendingFieldChange,
  cancelPendingFieldChange,
  enableFieldChangeConfirmation
} = useFieldChangeConfirm()

const onChange = (field) => {
  // Для полей с подтверждением значение будет установлено
  // и событие будет отправлено только после принятия изменений в диалоге.
  if (field.onChangeConfirm) return

  field.dispatch(EnumEventTypes.CHANGE)
  emit('change', field)
}

const applyPendingFieldChange = () => {
  // Сначала записываем подтверждённое значение, затем запускаем обычную
  // цепочку обработки изменения поля.
  const field = commitPendingFieldChange()
  if (!field) return

  field.dispatch(EnumEventTypes.CHANGE)
  emit('change', field)
}

const onButtonClick = (field) => {
  field.dispatch(EnumEventTypes.CLICK)
}

const onUpdate = (field, value) => {
  if (value === undefined) return

  // Некоторые редакторы отправляют новое значение отдельным update-событием.
  // Для подтверждаемых полей также откладываем его до решения пользователя.
  if (requestFieldUpdate(field, value)) return

  field.value = value
}

const isExpansionType = (type: string) => {
  return type === 'expansion'
}

// Только корневой список управляет событиями всей формы.
let removeFieldListeners = () => {}
let stopModelWatch = () => {}

onMounted(() => {
  if (!props.presentationOnly && uid !== undefined) {
    stopModelWatch = watch(() => props.modelValue, (model, previousModel, onCleanup) => {
      model.setUid(uid)
      const fields = model.getAllFields()
      const listeners = fields.map(field =>
        useEventListeners(model, field, config, setSectionVisible, uid, false)
      )
      let active = true
      removeFieldListeners = () => {
        active = false
        listeners.forEach(listener => listener.removeListeners())
      }
      onCleanup(removeFieldListeners)

      nextTick(() => {
        if (!active) return

        if (!props.listenersIsInitialized || previousModel) {
          fields.forEach(field => {
            field.dispatch(EnumEventTypes.INIT, { silent: true })
            if (field.options.changeOnStart) field.dispatch(EnumEventTypes.CHANGE)
          })
        }
        if (props.dispatchOnMount) dispatchInitEvents()
      })
    }, { immediate: true })
  }

  enableFieldChangeConfirmation()
})

onUnmounted(() => {
  stopModelWatch()
  removeFieldListeners()
})

const dispatchInitEvents = () => {
  sectionsRef.value.getAllFields().forEach(field => {
    field.dispatch(EnumEventTypes.CHANGE, { silent: true })
  })
}

defineExpose({ dispatchInitEvents })

</script>

<template>
  <LayoutContainer
    :grid="sectionsGrid"
    :active-areas="sections.filter(section => section.visible).map(section => section.gridArea)"
    variant="vuetify"
    :ignore-layout="ignoreLayout"
  >
    <LayoutItem
      v-for="section in sections"
      v-show="!sectionsGrid || section.visible"
      :key="section.id"
      :name="section.id"
      :area="section.gridArea"
      :layout="section.formLayout"
    >
      <Component
        :is="isExpansionType(section.type) ? VExpansionPanels : 'div'"
        v-show="section.visible"
        :class="['section', {'section-border': props.bordered}]"
        v-bind="isExpansionType(section.type) ? { 'model-value': section.expanded ? [0] : []} : {}"
      >
        <Component
          :is="isExpansionType(section.type) ? VExpansionPanel : 'div'"
          v-bind="isExpansionType(section.type) ? { eager: true } : {}"
        >
          <Component
            :is="isExpansionType(section.type) ? VExpansionPanelTitle : 'h3'"
            v-if="section.name && !hideSectionTitles"
            :class="['section__title', {'mb-2': section.type !== 'expansion'}]"
          >
            {{ section.name }}
          </Component>
          <Component :is="isExpansionType(section.type) ? VExpansionPanelText : 'div'">
            <LayoutContainer
              :grid="section.grid"
              :active-areas="getGridEntries(filterFields(section.fields), ignoreLayout ? undefined : section.grid?.groups).map(entry => entry.area)"
              :ignore-layout="ignoreLayout"
            >
              <FieldsGridContent
                v-slot="{ field }"
                :fields="filterFields(section.fields)"
                :groups="ignoreLayout ? undefined : section.grid?.groups"
              >
                <LayoutItem
                  :key="field.name"
                  :name="field.name"
                  :area="field.gridPlacement?.area"
                  :horizontal-align="field.gridPlacement?.horizontalAlign"
                  :vertical-align="field.gridPlacement?.verticalAlign"
                  :max-width="field.gridPlacement?.maxWidth"
                  :layout="field.layout"
                  :classes="`field-type-${field.type}`"
                >
                  <Component
                    :is="getComponentName(field.type)"
                    v-if="getComponentName(field.type) !== null"
                    :key="getEditableFieldKey(field)"
                    :field="getEditableField(field)"
                    :config="config"
                    :multiple="field.multipleValues"
                    :uuid="sectionsRef.params?.Uuid || uuid"
                    @change="() => onChange(field)"
                    @update="(v) => onUpdate(field, v)"
                    @set-section-visible="setSectionVisible"
                    @click="() => onButtonClick(field)"
                  >
                    <template #[field.name]="slotProps">
                      <slot
                        :name="field.name"
                        v-bind="slotProps"
                      />
                    </template>
                  </Component>
                </LayoutItem>
              </FieldsGridContent>
            </LayoutContainer>

            <div
              v-if="section.children.length"
              class="mt-4"
            >
              <FieldsList
                :model-value="props.modelValue"
                :display-sections="section.children"
                presentation-only
                :sections-grid="section.sectionsGrid"
                :ignore-layout="ignoreLayout"
                :hide-section-titles="hideSectionTitles"
                :uuid="sectionsRef.params?.Uuid || uuid"
                :config="config"
                @change="emit('change', $event)"
              />
            </div>
          </Component>
        </Component>
      </Component>
    </LayoutItem>
  </LayoutContainer>

  <FieldChangeConfirmDialog
    :model-value="pendingFieldChange !== null"
    :text="pendingFieldChange?.field.onChangeConfirmText || ''"
    @confirm="applyPendingFieldChange"
    @cancel="cancelPendingFieldChange"
  />
</template>

<style lang="scss" scoped>
.section {
  display: flex;
  flex-direction: column;

  &__title {
    font-size: 1.125rem;
    font-weight: bold;

    @media (max-width: 600px) {
      font-size: 1rem;
    }
  }
}

.v-row {
  margin: -0.5rem;
}

.v-col {
  padding: 0.5rem;
}

.section-border {
  height: 100%;
  padding: 20px 20px 28px;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  box-shadow: var(--ui-shadow-sm);
}
</style>
